import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Hotel, BadgePercent, MapPin, Users, Calendar, TrendingDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SearchResults = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    searchDeals();
  }, []);

  const searchDeals = async () => {
    try {
      setLoading(true);
      const searchData = {
        city: searchParams.get('city'),
        check_in: searchParams.get('checkIn'),
        check_out: searchParams.get('checkOut'),
        num_guests: parseInt(searchParams.get('guests')),
        max_budget: searchParams.get('maxBudget') ? parseFloat(searchParams.get('maxBudget')) : null
      };

      const response = await axios.post(`${API}/search`, searchData);
      setResults(response.data.results);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to search. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBookNow = (result) => {
    navigate('/booking', { state: { result, searchParams: Object.fromEntries(searchParams) } });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-white shadow-sm px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
          <div className="relative w-11 h-11 bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-md">
            <Hotel className="w-6 h-6 text-white" />
            <div className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center shadow-sm border-2 border-white">
              <BadgePercent className="w-3.5 h-3.5 text-white" />
            </div>
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">traveldiscount.ai</h1>
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate('/')}>
          New Search
        </Button>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Search Summary */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Last-Minute Deals in {searchParams.get('city')}</h2>
          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {searchParams.get('checkIn')} → {searchParams.get('checkOut')}
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              {searchParams.get('guests')} guests
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Searching for best deals...</p>
          </div>
        ) : results.length === 0 ? (
          <Card className="p-12 text-center">
            <TrendingDown className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No deals found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search criteria or different dates</p>
            <Button onClick={() => navigate('/')}>Back to Search</Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-6" data-testid="search-results-list">
            {results.map((result, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-shadow" data-testid={`result-card-${index}`}>
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Property Image Placeholder */}
                  <div className="w-full md:w-64 h-48 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center">
                    <MapPin className="w-12 h-12 text-blue-600" />
                  </div>

                  {/* Property Details */}
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{result.property.name}</h3>
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <MapPin className="w-4 h-4" />
                          {result.property.city}
                        </div>
                      </div>
                      <div className="savings-badge">
                        Save {result.savings_percent}%
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="font-semibold text-gray-900">{result.room.name}</p>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Users className="w-4 h-4" />
                        Up to {result.room.capacity} guests
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-3xl font-bold text-blue-600">
                            €{result.price_per_night}
                          </span>
                          <span className="text-gray-500">/night</span>
                        </div>
                        <div className="text-sm text-gray-500">
                          <span className="line-through">€{result.external_price}</span>
                          <span className="ml-2 text-green-600 font-semibold">
                            Total: €{result.total_price} ({result.nights} nights)
                          </span>
                        </div>
                      </div>
                      <Button 
                        onClick={() => handleBookNow(result)}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid={`book-now-btn-${index}`}
                      >
                        Book Now
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResults;